import React, { useState, useEffect, useMemo } from 'react';
import FormStep from "./FormStep";
import Input from "../components/form/Input";

const Confirmation = ({ prevStep, nextStep, formData, setFormData }) => {
  console.log('Confirmation component received formData:', formData);
  console.log('Confirmation received invoiceDetails:', formData.invoiceDetails);
  
  // Use useMemo to avoid recreating initialInvoiceDetails on every render
  const initialInvoiceDetails = useMemo(() => {
    const invoiceDetails = formData.invoiceDetails || {};
    console.log('Initializing invoiceDetails from formData:', invoiceDetails);
    
    // Check if invoiceDetails is empty and use default values if needed
    if (!invoiceDetails.companyName && !invoiceDetails.addressLine1) {
      return {
        companyName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        postcode: ""
      };
    }
    
    // Ensure all expected fields exist
    return {
      companyName: invoiceDetails.companyName || "",
      addressLine1: invoiceDetails.addressLine1 || "",
      addressLine2: invoiceDetails.addressLine2 || "",
      city: invoiceDetails.city || "",
      postcode: invoiceDetails.postcode || ""
    };
  }, [formData.invoiceDetails]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showInvoiceOption, setShowInvoiceOption] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState(initialInvoiceDetails);
  // Purchase Order number state (optional)
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(formData.invoiceDetails?.purchaseOrder || "");
  // Always require users to re-accept terms for legal compliance
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});

  // Debug log
  useEffect(() => {
    console.log('Confirmation component mounted');
    console.log('formData:', formData);
    console.log('initialInvoiceDetails:', initialInvoiceDetails);
    console.log('invoiceDetails state:', invoiceDetails);
    
    // Check if we have a booking ID in localStorage (from a previous Stripe redirect)
    const storedBookingId = localStorage.getItem('lde_booking_id');
    if (storedBookingId && !formData.booking_id && setFormData) {
      console.log('Retrieved booking ID from localStorage:', storedBookingId);
      setFormData(prevFormData => ({
        ...prevFormData,
        booking_id: parseInt(storedBookingId, 10)
      }));
    }
  }, [formData, initialInvoiceDetails, invoiceDetails, setFormData]);

  const handleInvoiceChange = (e) => {
    setInvoiceDetails(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    
    // Clear error for this field if it exists
    if (errors[e.target.name]) {
      setErrors(prev => ({
        ...prev,
        [e.target.name]: null
      }));
    }
  };

  const validateFields = () => {
    const newErrors = {};
    
    // Only validate company name and address line 1 if they're required
    if (!invoiceDetails.companyName.trim()) {
      newErrors.companyName = { message: 'Company name is required' };
    }
    
    if (!invoiceDetails.addressLine1.trim()) {
      newErrors.addressLine1 = { message: 'Address line 1 is required' };
    }
    
    if (!invoiceDetails.city.trim()) {
      newErrors.city = { message: 'City/Town is required' };
    }
    
    // if (!invoiceDetails.postcode.trim()) {
    //   newErrors.postcode = { message: 'Postcode is required' };
    // } else {
    //   // UK postcode regex pattern
    //   // ^([A-Z][A-HJ-Y]?[0-9][A-Z0-9]? ?[0-9][A-Z]{2}|GIR ?0A{2})$
    //   const ukPostcodeRegex = /^([A-Z][A-HJ-Y]?[0-9][A-Z0-9]? ?[0-9][A-Z]{2}|GIR ?0A{2})$/;
    //   if (!ukPostcodeRegex.test(invoiceDetails.postcode.trim())) {
    //     newErrors.postcode = { message: 'Please enter a valid UK postcode' };
    //   }
    // }
    
    if (!acceptedTerms) {
      newErrors.terms = { message: 'You must accept the terms and conditions' };
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calculate totals based on ticket type
  const getTicketPrice = () => {
    const { event, ticketType } = formData;
    
    if (!event) return 0;
    
    switch (ticketType) {
      case 'early_bird':
        return event.earlyBirdPrice || event.price || 0;
      case 'local_authority':
        return event.localAuthorityPrice || event.price || 0;
      case 'standard':
        return event.price || 0;
      default:
        // Check if it's a custom ticket type
        if (event.customTickets && Array.isArray(event.customTickets)) {
          const customTicket = event.customTickets.find(ticket => ticket.id === ticketType);
          if (customTicket && customTicket.price) {
            return customTicket.price;
          }
        }
        return event.price || 0;
    }
  };
  
  const ticketPrice = getTicketPrice();
  const eventName = formData.event?.name || 'Event';
  const ticketTypeName = (() => {
    const { ticketType, event } = formData;
    
    // Standard ticket types
    switch (ticketType) {
      case 'early_bird': return 'Early Bird';
      case 'local_authority': return 'Local Authority';
      case 'standard': return 'Standard';
    }
    
    // Check if it's a custom ticket type
    if (event && event.customTickets && Array.isArray(event.customTickets)) {
      const customTicket = event.customTickets.find(ticket => ticket.id === ticketType);
      if (customTicket && customTicket.name) {
        return customTicket.name;
      }
    }
    
    return 'Standard';
  })();
  
  // Check if there's a primary attendee
  const isPrimaryAttendee = formData.bookingFor === "Myself" || formData.bookingFor === "Myself & others";
  
  // Calculate total based on delegates and primary attendee if present
  const subtotal = (isPrimaryAttendee ? ticketPrice : 0) + 
                formData.delegates.reduce((total, _delegate) => total + ticketPrice, 0);
  
  // VAT is now added to the price (20%)
  const vatRate = 0.2; // 20% VAT
  const vatAmount = round(subtotal * vatRate, 2);
  
  // The total is the subtotal plus VAT
  const total = subtotal + vatAmount;
  
  // Helper function to round to 2 decimal places
  function round(value, decimals) {
    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
  }

  const handlePayment = async (method) => {
    if (!validateFields()) {
      return;
    }
    
    setIsProcessing(true);
    setError(null);

    try {
      // Extract event data for formatting
      const { event } = formData;
      let eventData = {};
      
      if (event) {
        // Format event data correctly for API submission
        eventData = {
          event_id: event.id || event.event_id,
          date_id: event.dateId || event.date_id || 0,
          price: getTicketPrice(), // Use the price based on ticket type
          name: event.name,
          blogId: event.blogId || 1,
          // Ensure we send custom tickets data to the server
          customTickets: event.customTickets || []
        };
        
        // Log if we have custom tickets for debugging
        if (event.customTickets && Array.isArray(event.customTickets)) {
          console.log('Custom tickets being sent to server:', event.customTickets);
        } else {
          console.log('No custom tickets available in formData');
        }
      }
      
      // Prepare the data for submission
      const submitData = {
        ...formData,
        event: eventData,
        paymentMethod: method,
        invoiceDetails: invoiceDetails,
        purchaseOrder: purchaseOrderNumber
      };
      
      // Log data being sent to the server
      console.log('Submitting booking data:', submitData);

      // Send booking data to WordPress
      const response = await fetch('/wp-json/lde/v1/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();
      console.log('Response from server:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      // Store the booking ID in form data for future requests
      if (data.bookingId && setFormData) {
        console.log('Saving booking ID to form data:', data.bookingId);
        
        // Also save to localStorage for persistence across page reloads
        try {
          localStorage.setItem('lde_booking_id', data.bookingId.toString());
          console.log('Booking ID saved to localStorage');
        } catch (storageErr) {
          console.error('Failed to save booking ID to localStorage:', storageErr);
        }
        
        // Update the form data (this might not persist through navigation)
        setFormData(prevFormData => ({
          ...prevFormData,
          booking_id: data.bookingId
        }));
      }

      if (method === 'card') {
        // Ensure a small delay to allow state updates to complete
        setTimeout(() => {
          // Redirect to Stripe Checkout
          window.location.href = data.stripeUrl;
        }, 100);
      } else {
        // Invoice payment - check if we have a redirectUrl in the response
        if (data.redirectUrl) {
          console.log('Redirecting to:', data.redirectUrl);
          
          // Clear the stored booking ID if redirecting to thank you page
          if (data.redirectUrl.includes('thank-you')) {
            localStorage.removeItem('lde_booking_id');
          }
          
          window.location.href = data.redirectUrl;
        } else {
          // Fallback to nextStep if no redirectUrl
          console.log('No redirectUrl in response, using nextStep');
          nextStep(); 
        }
      }

    } catch (err) {
      setError(err.message);
      console.error('Payment processing error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <FormStep 
      title="Invoice Details" 
      onBack={null}
    >
      <div className="event-summary">
        <h3>Event: {eventName}</h3>
        {formData.event?.date && (
          <p>Date: {(() => {
            const date = new Date(formData.event.date);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
          })()}</p>
        )}
        {formData.event?.venue && <p>Venue: {formData.event.venue}</p>}
      </div>

      <div className="invoice-details-form">
        <div className="form-row">
          <Input
            id="companyName"
            name="companyName"
            label="Company Name"
            value={invoiceDetails.companyName}
            onChange={handleInvoiceChange}
            error={errors.companyName}
            required={true}
          />
        </div>
        
        <div className="form-row address-row">
          <div className="col-6">
            <Input
              id="addressLine1"
              name="addressLine1"
              label="Address Line 1"
              value={invoiceDetails.addressLine1}
              onChange={handleInvoiceChange}
              error={errors.addressLine1}
              required={true}
            />
          </div>
          <div className="col-6">
            <Input
              id="addressLine2"
              name="addressLine2"
              label="Address Line 2"
              value={invoiceDetails.addressLine2}
              onChange={handleInvoiceChange}
              error={errors.addressLine2}
            />
          </div>
        </div>
        
        <div className="form-row address-row">
          <div className="col-6">
            <Input
              id="city"
              name="city"
              label="City / Town"
              value={invoiceDetails.city}
              onChange={handleInvoiceChange}
              error={errors.city}
              required={true}
            />
          </div>
          <div className="col-6">
            <Input
              id="postcode"
              name="postcode"
              label="Postcode"
              value={invoiceDetails.postcode}
              onChange={handleInvoiceChange}
              error={errors.postcode}
              required={true}
            />
          </div>
        </div>
        
        <div className="order-summary-container">
          <h3 className="summary-title">Order Summary</h3>
          
          <table className="order-table">
            <tbody>
              {/* Include primary attendee ticket if it exists */}
              {isPrimaryAttendee && (
                <tr>
                  <td>Primary "{formData.firstName} {formData.lastName}" {ticketTypeName} Ticket</td>
                  <td className="price-column">£{ticketPrice.toFixed(2)}</td>
                </tr>
              )}
              
              {/* Include delegate tickets */}
              {formData.delegates.map((delegate, index) => (
                <tr key={index}>
                  <td>Delegate "{delegate.firstName} {delegate.lastName}" {ticketTypeName} Ticket</td>
                  <td className="price-column">£{ticketPrice.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="subtotal-row">
                <td>Subtotal (excl. VAT):</td>
                <td className="price-column">£{subtotal.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
              </tr>
              <tr className="vat-row">
                <td>VAT (20%):</td>
                <td className="price-column">£{vatAmount.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
              </tr>
              <tr className="total-row">
                <td>Total Payment Due:</td>
                <td className="price-column">£{total.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        
        <div className={`terms-container ${errors.terms ? 'has-error' : ''}`}>
          <label className={`checkbox-container ${errors.terms ? 'has-error' : ''}`}>
            <input 
              type="checkbox" 
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                if (e.target.checked && errors.terms) {
                  setErrors(prev => ({ ...prev, terms: null }));
                }
              }}
            />
            <span className="checkmark"></span>
            <span className="label-text">
              I agree to the <a 
                href={window.ldEventsConfig?.termsUrl || '/terms-and-conditions'} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                terms and conditions
              </a>
            </span>
          </label>
          {errors.terms && <div className="error-msg"><i className="icon-attention"></i> {errors.terms.message}</div>}
        </div>
        
        <div className="payment-options-container">
          <button 
            className={`pay-by-card-btn ${isProcessing ? 'is-spinning' : ''}`}
            onClick={() => handlePayment('card')}
            disabled={isProcessing || showInvoiceOption}
          >
            PAY BY CARD
          </button>
          
          {/* Only show invoice option if enabled for this event */}
          {formData.event?.enableInvoicePayment !== false && (
            <div className="invoice-option-container">
              <p>Unable to pay by card? We can send you an invoice <button
                type="button"
                className="invoice-link-btn"
                onClick={() => setShowInvoiceOption(true)}
                disabled={isProcessing}
              >
                Please click here
              </button></p>
            
            {showInvoiceOption && (
              <>
                <div className="invoice-disclaimer">
                  <h4>To pay by invoice please confirm that you accept the following terms and conditions:</h4>
                  <ul>
                    <li>This booking is final and cannot be cancelled but delegate name changes are permitted until 24 hours prior to the start of the event.</li>
                    <li>The invoice must be paid within 30 days of receipt (today) otherwise the discounted ticket rate will be lost, and the full fee of £799 plus VAT per delegate will be due.</li>
                    <li>If you need a Purchase Order number or reference listed on the invoice, please include these in the box below, or alternatively leave it blank</li>
                  </ul>
                </div>

                <div className="form-row address-row">
                  <Input
                    id="purchaseOrder"
                    name="purchaseOrder"
                    label="Purchase Order Number (optional)"
                    value={purchaseOrderNumber}
                    onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                    error={errors.purchaseOrder}
                  />
                </div>
          
                <button
                  className={`pay-by-invoice-btn ${isProcessing ? 'is-spinning' : ''}`}
                  onClick={() => handlePayment('invoice')}
                  disabled={isProcessing}
                >
                        PAY BY INVOICE
                </button>
              </>
            )}
            </div>
          )}
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>
    </FormStep>
  );
};

export default Confirmation;
